import classNames from "classnames";
import { timingFunctions } from "polished";
import { useEffect, useMemo, useState } from "react";
import { useFormBag } from "react-yup";

import { SvgSprite, TooltipPopover } from "@skv/core";
import { BaseFieldProps, HelpText, ValidationError } from ".";

import {
  Select as AriaSelect,
  SelectItem,
  SelectItemCheck,
  SelectLabel,
  SelectPopover,
  SelectState,
  useSelectState,
} from "ariakit/select";

type SelectPropsOmit = Omit<
  React.DetailedHTMLProps<
    React.SelectHTMLAttributes<HTMLSelectElement>,
    HTMLSelectElement
  >,
  "id" | "name"
>;

interface SelectProps extends BaseFieldProps, SelectPropsOmit {
  selectProps?: {
    className?: string;
  };
}

export const Select: React.FC<SelectProps> = ({
  name,
  id,
  label,
  labelProps,
  selectProps,
  helpText,
  optional,
  children,
  ...rest
}) => {
  const { getValue, getError, isTouched } = useFormBag();

  const value = getValue(name) || "";
  const error = getError(name);
  const touched = isTouched(name);

  return (
    <div className="nsw-form__group">
      <label
        htmlFor={id}
        {...labelProps}
        className={classNames("nsw-form__label", labelProps?.className)}
      >
        {label}{" "}
        {optional ? <small className="text-muted">(optional)</small> : "*"}
      </label>
      {helpText && (
        <HelpText
          id={`${id}-help-text`}
          helpText={helpText}
          className="nsw-form__helper"
        />
      )}
      <select
        id={id}
        className={classNames("nsw-form__select", selectProps?.className, {
          "is-invalid": error && touched,
        })}
        name={name}
        value={value as string}
        aria-invalid={!!error && touched}
        aria-required={!optional}
        aria-describedby={`${error && touched ? `${id}-error-message ` : ""}${
          helpText ? `${id}-help-text` : ""
        }`}
        {...rest}
      >
        {children}
      </select>
      {error && touched && (
        <ValidationError errorId={`${id}-error-message`} message={error} />
      )}
    </div>
  );
};

type BaseItem = {
  id: string;
  name: string;
};

type LabelProps = React.DetailedHTMLProps<
  React.LabelHTMLAttributes<HTMLLabelElement>,
  HTMLLabelElement
>;

interface SelectMultiProps<T extends BaseItem> {
  id: string;
  label: string;
  labelProps?: LabelProps;
  name: string;
  items: T[];
  defaultValue?: string | string[] | undefined;
  categoryTitle?: string;
  categoryType?: string;
  className?: string;
  onUpdateField: (value: string) => void;
  onClear?: () => void;
  onDropdownOpen?: () => void;
  renderValue?: (
    selectState: SelectState<string | string[]>,
    itemMap: Map<string, T>
  ) => React.ReactNode;
}

export const SelectMulti = <T extends BaseItem>({
  id,
  label,
  labelProps,
  items,
  name,
  className,
  categoryTitle = "Nothing selected",
  categoryType = "items selected",
  onUpdateField,
  renderValue,
  onClear,
  onDropdownOpen,
  defaultValue = [],
}: SelectMultiProps<T>) => {
  const selectState = useSelectState<string | string[]>({
    value: defaultValue,
    sameWidth: true,
  });

  const itemMap = useMemo(() => {
    const map = new Map<string, T>();
    items.forEach((item) => {
      map.set(item.id, item);
    });

    return map;
  }, [items]);

  const [isTooltipVisible, setIsTooltipVisible] = useState(false);

  useEffect(() => {
    if (selectState.mounted) {
      onDropdownOpen && onDropdownOpen();
    }
  }, [selectState.mounted, onDropdownOpen]);

  return (
    <div className={classNames(className, "flex-grow-1")}>
      <SelectLabel
        htmlFor={id}
        as="label"
        className={classNames("nsw-form__label", labelProps?.className)}
        state={selectState}
      >
        {label}
      </SelectLabel>
      <div
        className="position-relative bg-white"
        css={`
          border: 1px solid var(--nsw-grey-01);
          border-radius: var(--nsw-border-radius);
        `}
      >
        <div
          css={`
            &:focus-within .nsw-form__select {
              outline: solid 3px var(--nsw-focus);
              outline-offset: 3px;
            }
          `}
        >
          <AriaSelect
            id={id}
            state={selectState}
            className="select nsw-form__select overflow-hidden border-0"
            css={`
              display: flex;
              align-items: center;
              justify-content: space-between;
              line-height: 1.5;

              white-space: nowrap;
              gap: 0.25em;

              width: ${onClear && selectState.value.length
                ? "calc(100% - 2.75rem)"
                : "100%"};
            `}
          >
            <span className="text-truncate mr-4">
              {(() => {
                if (renderValue) {
                  return renderValue(selectState, itemMap);
                }

                if (selectState.value.length === 0) {
                  return categoryTitle;
                }

                if (selectState.value.length === 1) {
                  return itemMap.get(selectState.value[0])?.name;
                }

                return `${selectState.value.length} ${categoryType}`;
              })()}
            </span>
          </AriaSelect>
          {selectState.mounted && (
            <SelectPopover
              state={selectState}
              className="bg-white border py-1 overflow-auto"
              css={`
                max-height: 300px;
                z-index: 1000;
                display: flex;
                flex-direction: column;
                overscroll-behavior: contain;
                border-radius: 2px;
              `}
            >
              {items.map((x) => (
                <SelectItem
                  key={x.id}
                  value={x.id}
                  className="d-flex align-items-center px-2 py-1"
                  onClick={() => {
                    onUpdateField(x.id);
                  }}
                  css={`
                    cursor: pointer;
                    line-height: 2;
                    font-size: 16px;
                    font-weight: 400;

                    text-decoration: underline;

                    &[data-active-item] {
                      background-color: #e5e9ef;
                    }

                    &:hover {
                      background-color: #e5e9ef;
                    }
                  `}
                >
                  <SelectItemCheck />
                  {x.name}
                </SelectItem>
              ))}
            </SelectPopover>
          )}
        </div>
        {onClear && (
          // Note - this wrapping div is required for screens zoomed in >= 400% to prevent overflow from the clear button.
          <div
            className="position-absolute d-flex justify-content-start align-items-center"
            css={`
              top: 0;
              right: 0;
              bottom: 0;
              left: ${selectState.value.length
                ? "calc(100% - 2.75rem)"
                : "100%"};
              overflow: ${selectState.value.length ? "visible" : "hidden"};
              transition: all 300ms ${timingFunctions("easeOutCubic")};
            `}
          >
            <TooltipPopover
              placement="top"
              tooltipText="Clear"
              show={isTooltipVisible}
              onToggle={(nextShow) => setIsTooltipVisible(nextShow)}
            >
              <span
                role="button"
                className="btn text-muted border-0 p-0"
                tabIndex={selectState.value.length ? 0 : -1}
                css={`
                  opacity: ${selectState.value.length ? "1" : "0"};
                  transition: all 300ms ${timingFunctions("easeOutCubic")};

                  &:hover > .rounded-circle {
                    background-color: ${(p): string => p.theme.gray200};
                    box-shadow: 1px 1px 3px ${(p): string => p.theme.gray400};
                  }
                `}
                onClick={onClear}
                onKeyDown={(e) => {
                  if (e.key === " " || e.key === "Enter") {
                    e.preventDefault();
                    onClear();
                    setIsTooltipVisible(false);
                  }
                }}
              >
                <span
                  className="rounded-circle embed-responsive embed-responsive-1by1 p-3"
                  css={`
                    transition: all 300ms ${timingFunctions("easeOutCubic")};

                    &:hover {
                      background-color: ${(p): string => p.theme.gray200};
                      box-shadow: 1px 1px 3px ${(p): string => p.theme.gray400};
                    }
                  `}
                >
                  <SvgSprite
                    id="icons:close"
                    width={14}
                    height={14}
                    aria-label="Clear"
                    className="img-fluid embed-responsive-item p-2"
                  />
                </span>
              </span>
            </TooltipPopover>
          </div>
        )}
      </div>
      <input
        disabled={selectState.value.length === 0}
        type="hidden"
        name={name}
        value={selectState.value}
      />
    </div>
  );
};
